import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ClipboardList, Plus } from 'lucide-react'
import { Panel, SectionHeader } from '@/components/erp/primitives'
import { EmptyState, ErrorStrip, Forbidden, LoadingBlock, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { fmtDate, fmtDateTime } from '@/shared/lib/format'
import type { SurveyAssignmentDto } from '@/shared/api/types'
import { myAssignments } from './api'
import { AttemptDialog } from './AttemptDialog'
import { AttemptsPanel } from './AttemptsPanel'
import {
  Pill, assignmentStatusLabel, count, pollStatusLabel, pollStatusTone,
} from './vocab'

/**
 * The interviewer's own worklist.
 *
 * ## Why this page passes no id
 *
 * `GET /surveys/my-assignments` resolves the caller from their token. An
 * interviewer never types, picks or is handed their own mobilizer id — a worklist
 * that took one would be a worklist somebody could point at another person's
 * work, and the authority that guards it (`survey:field`) is about conducting
 * interviews, not about reading the roster.
 *
 * ## Cards, not a grid
 *
 * The supervisor's Fieldwork screen is a table because a supervisor compares
 * rows. An interviewer has three assignments and needs one thing from each: what
 * am I doing, how far in am I, and where do I press to start. A nine-column grid
 * on a phone in the field answers none of those.
 */
export function MyAssignmentsPage() {
  const canField = useCan('survey:field')
  const [recording, setRecording] = useState<SurveyAssignmentDto | null>(null)
  const [expanded, setExpanded] = useState<string | null>(null)
  /** Bumped on every recorded attempt so an open attempts list refreshes with it. */
  const [recorded, setRecorded] = useState(0)

  const mine = useLoad(() => myAssignments(), [])

  if (!canField) return <Forbidden what="the interviewer worklist" />

  const rows = mine.data ?? []

  return (
    <div className="space-y-4">
      <SectionHeader
        title="My work"
        description="The polls you are fielding, and the attempts you have logged against each."
      />

      <ErrorStrip message={mine.error} />

      {mine.loading && rows.length === 0 ? (
        <LoadingBlock label="Loading your assignments…" />
      ) : rows.length === 0 ? (
        <Panel>
          <EmptyState
            icon={<ClipboardList className="h-4 w-4" />}
            title="No assignments yet"
            hint={
              // Deliberately not "something went wrong" and not "you have no
              // data". An empty worklist is the ordinary state of a new
              // interviewer and of one between polls; the only useful next step
              // is a person, so the empty state names them rather than offering a
              // reload button that will return the same empty list.
              'Nobody has put you on a poll. That is a supervisor\'s job, not a fault on this '
              + 'screen — ask yours to assign you, and this list fills in as soon as they do.'
            }
          />
        </Panel>
      ) : (
        <div className="grid grid-cols-12 gap-4">
          {rows.map((a) => (
            <div key={a.id} className="col-span-12 lg:col-span-6">
              <AssignmentCard
                assignment={a}
                expanded={expanded === a.id}
                reloadToken={recorded}
                onToggle={() => setExpanded(expanded === a.id ? null : a.id)}
                onRecord={() => setRecording(a)}
              />
            </div>
          ))}
        </div>
      )}

      {recording && (
        <AttemptDialog
          open
          surveyId={recording.surveyId}
          assignment={recording}
          onClose={() => setRecording(null)}
          onSaved={() => {
            setRecording(null)
            // The card's collected count comes off the assignment read, and the
            // attempts list under it comes off its own; both have just moved.
            setRecorded((n) => n + 1)
            mine.reload()
          }}
        />
      )}
    </div>
  )
}

function AssignmentCard({ assignment, expanded, reloadToken, onToggle, onRecord }: {
  assignment: SurveyAssignmentDto
  expanded: boolean
  reloadToken: number
  onToggle: () => void
  onRecord: () => void
}) {
  const a = assignment
  const revoked = a.statusCode === 'REVOKED'

  return (
    <Panel
      // The poll's NAME heads the card. It used to be `surveyRef ?? 'Poll'`, and
      // the server sent no ref on this endpoint at all — so every card on an
      // interviewer's screen read "Poll", which is the one thing they already
      // knew. The ref moves to the subtitle beside the ward, where it is useful
      // for saying which poll to a supervisor over the phone.
      title={a.surveyTitle ?? a.surveyRef ?? 'Poll'}
      description={[a.surveyRef, a.scopeUnitName].filter(Boolean).join(' · ') || undefined}
      right={(
        <div className="flex items-center gap-1.5">
          {a.surveyStatusCode && a.surveyStatusCode !== 'LIVE' && (
            <Pill tone={pollStatusTone(a.surveyStatusCode)}>
              {pollStatusLabel(a.surveyStatusCode)}
            </Pill>
          )}
          <Pill>{assignmentStatusLabel(a.statusCode)}</Pill>
        </div>
      )}
    >
      <div className="space-y-3">
        {a.surveyDescription && (
          // What the poll is FOR, in the words whoever commissioned it wrote. An
          // interviewer who knows that answers a doorstep question about it
          // instead of guessing, and the description is the only place on this
          // screen that says anything about the subject at all.
          <p className="text-erp-sm text-muted-foreground">{a.surveyDescription}</p>
        )}

        {a.surveyStatusCode && a.surveyStatusCode !== 'LIVE' && !revoked && (
          <Note tone="bad">
            This poll is {pollStatusLabel(a.surveyStatusCode).toLowerCase()}, not live, so it is
            not accepting responses — capture will refuse a submission with a reason. The
            assignment is still yours; wait for your supervisor to launch it rather than walking
            a route it cannot record.
          </Note>
        )}
        <dl className="grid gap-3"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))' }}>
          <Cell label="Collected" value={count(a.collected)} />
          <Cell label="Target" value={a.targetCount === 0 ? '—' : count(a.targetCount)} />
          <Cell label="Due" value={a.dueAt ? fmtDate(a.dueAt) : '—'} />
          <Cell label="Stratum" value={a.stratumCode ?? '—'} />
        </dl>

        {revoked ? (
          <Note tone="bad">
            Revoked{a.revokedAt ? ` on ${fmtDateTime(a.revokedAt)}` : ''}
            {a.revokeReason ? `: ${a.revokeReason}` : '.'} No further attempts can be recorded
            against it, and the ones you already logged stay — they are the record of what was
            tried, and the poll's response rate reads them.
          </Note>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            {/*
              The capture screen is another agent's; this page only knows the two
              things it needs to hand over — which poll, and which assignment the
              interview should be attributed to.
            */}
            <Link
              className="erp-btn erp-btn-primary h-8 text-erp-xs"
              to={`/polling/capture?poll=${a.surveyId}&assignment=${a.id}`}
            >
              Start interviewing
            </Link>
            <button type="button" className="erp-btn h-8 text-erp-xs" onClick={onRecord}>
              <Plus className="h-3 w-3" /> Record an attempt
            </button>
            <button type="button" className="erp-btn erp-btn-ghost h-8 text-erp-xs"
              aria-expanded={expanded} onClick={onToggle}>
              {expanded ? 'Hide attempts' : 'Attempts so far'}
            </button>
          </div>
        )}

        {!revoked && (
          <p className="text-erp-xs text-muted-foreground">
            Log an attempt for every door you knock on, not only the ones that turn into
            interviews. A refusal and an empty house are what the response rate is measured
            against; unlogged, they look like doors nobody went to.
          </p>
        )}

        {expanded && (
          <div className="pt-1 border-t border-border">
            <div className="pt-3">
              <AttemptsPanel
                surveyId={a.surveyId}
                assignment={a}
                // The card already carries the record action; a second button
                // inside the list would be the same action twice on one card.
                canRecord={false}
                reloadToken={reloadToken}
              />
            </div>
          </div>
        )}
      </div>
    </Panel>
  )
}

function Cell({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-erp-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="text-erp-md tabular-nums mt-0.5">{value}</dd>
    </div>
  )
}
